import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  FileText,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Eye,
  PenLine,
  CreditCard,
  FileCheck,
  ClipboardCheck,
  Clock,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_META = {
  contractor_agreement: { label: 'Contractor Agreement', desc: 'Independent Contractor Agreement & Annexure A (Pages 1-14)', icon: FileText, color: 'from-[#1B365D] to-[#2a4a7a]' },
  referral_agreement: { label: 'Referral Fee Agreement', desc: 'Annexure B - Referral Fee Policy (Pages 16-18)', icon: FileCheck, color: 'from-emerald-600 to-teal-500' },
  direct_deposit: { label: 'Direct Deposit Authorization', desc: 'ACH Authorization Terms (Page 15)', icon: CreditCard, color: 'from-amber-500 to-orange-500' },
  w9: { label: 'W-9 Form Instructions', desc: 'IRS W-9 Instructions & Certification (Pages 19-24)', icon: ClipboardCheck, color: 'from-violet-600 to-purple-500' }
};

const TEMPLATE_ORDER = ['contractor_agreement', 'referral_agreement', 'direct_deposit', 'w9'];

const SectionEditor = ({ section, index, onChange, onDelete }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`section-${index}`}>
      <div
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <span className="font-medium text-sm text-slate-800 flex-1 truncate">{section.heading || `Section ${index + 1}`}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
          data-testid={`delete-section-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Section Heading</Label>
                <Input
                  value={section.heading}
                  onChange={(e) => onChange({ ...section, heading: e.target.value })}
                  placeholder="e.g., 1. SCOPE"
                  className="font-medium"
                  data-testid={`heading-${index}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Content</Label>
                <textarea
                  value={section.body}
                  onChange={(e) => onChange({ ...section, body: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0071E3] focus:border-transparent resize-y"
                  placeholder="Enter the section content..."
                  data-testid={`body-${index}`}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TemplatePreview = ({ template, onClose }) => {
  if (!template) return null;
  const meta = TEMPLATE_META[template.template_key] || {};

  return (
    <Dialog open={!!template} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#1B365D]" />
            Preview: {meta.label}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          <div className="prose prose-sm prose-slate max-w-none space-y-4 p-4 bg-slate-50 rounded-xl">
            {template.sections?.map((s, i) => (
              <div key={i}>
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">{s.heading}</h4>
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DocumentTemplates = () => {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState(TEMPLATE_ORDER[0]);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/templates`, getAuthHeaders());
      setTemplates(res.data);
      if (res.data[activeTemplate]) {
        setEditData(JSON.parse(JSON.stringify(res.data[activeTemplate])));
      }
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, activeTemplate]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const switchTemplate = (key) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Switch anyway?')) return;
    }
    setActiveTemplate(key);
    setEditData(JSON.parse(JSON.stringify(templates[key] || {})));
    setHasChanges(false);
  };

  const updateSection = (idx, section) => {
    const updated = { ...editData, sections: editData.sections.map((s, i) => i === idx ? section : s) };
    setEditData(updated);
    setHasChanges(true);
  };

  const deleteSection = (idx) => {
    if (!window.confirm('Delete this section?')) return;
    const updated = { ...editData, sections: editData.sections.filter((_, i) => i !== idx) };
    setEditData(updated);
    setHasChanges(true);
  };

  const addSection = () => {
    const updated = { ...editData, sections: [...(editData.sections || []), { heading: '', body: '' }] };
    setEditData(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/templates/${activeTemplate}`, {
        title: editData.title,
        sections: editData.sections
      }, getAuthHeaders());
      toast.success('Template saved successfully');
      setHasChanges(false);
      fetchTemplates();
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this template to the default? This cannot be undone.')) return;
    try {
      await axios.post(`${API_URL}/api/templates/${activeTemplate}/reset`, {}, getAuthHeaders());
      toast.success('Template reset to default');
      setHasChanges(false);
      fetchTemplates();
    } catch (e) {
      toast.error('Failed to reset template');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" /></div>;

  const meta = TEMPLATE_META[activeTemplate];
  const Icon = meta?.icon || FileText;

  return (
    <div className="space-y-6" data-testid="document-templates-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Document Templates</h1>
        <p className="text-slate-500 mt-1">Edit the onboarding agreement terms that agents see during sign-up</p>
      </div>

      <div className="flex gap-6">
        {/* Template Selector - Left sidebar */}
        <div className="w-64 flex-shrink-0 space-y-2" data-testid="template-selector">
          {TEMPLATE_ORDER.map(key => {
            const m = TEMPLATE_META[key];
            const TIcon = m.icon;
            const isActive = activeTemplate === key;
            const t = templates[key];
            return (
              <button
                key={key}
                onClick={() => switchTemplate(key)}
                className={`w-full text-left p-3 rounded-xl transition-all ${isActive ? 'bg-[#1B365D] text-white shadow-lg' : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'}`}
                data-testid={`template-btn-${key}`}
              >
                <div className="flex items-center gap-2.5">
                  <TIcon className={`w-4 h-4 ${isActive ? 'text-white/80' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{m.label}</p>
                    <p className={`text-[10px] mt-0.5 ${isActive ? 'text-white/60' : 'text-slate-400'}`}>
                      {t?.is_default ? 'Default' : 'Customized'}
                      {t?.updated_at && !t.is_default && ` · ${new Date(t.updated_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  {!t?.is_default && <Badge className="bg-[#0071E3] text-white text-[8px] px-1.5">Edited</Badge>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Editor - Main area */}
        <div className="flex-1 space-y-4">
          {/* Template header */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className={`h-2 bg-gradient-to-r ${meta.color}`} />
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="font-heading text-lg">{meta.label}</CardTitle>
                    <CardDescription className="text-xs">{meta.desc}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Unsaved
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => setPreviewTemplate(editData)} data-testid="preview-btn">
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full text-slate-500" onClick={handleReset} data-testid="reset-btn">
                    <RotateCcw className="w-4 h-4 mr-1" /> Reset
                  </Button>
                  <Button size="sm" className="rounded-full bg-[#1B365D] hover:bg-[#1B365D]/90" onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-template-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Document Title */}
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Document Title</Label>
            <Input
              value={editData?.title || ''}
              onChange={(e) => { setEditData({ ...editData, title: e.target.value }); setHasChanges(true); }}
              className="font-semibold"
              data-testid="template-title"
            />
          </div>

          {/* Sections */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-500">Sections ({editData?.sections?.length || 0})</Label>
            </div>
            {editData?.sections?.map((section, idx) => (
              <SectionEditor
                key={idx}
                section={section}
                index={idx}
                onChange={(s) => updateSection(idx, s)}
                onDelete={() => deleteSection(idx)}
              />
            ))}
            <Button variant="outline" className="w-full rounded-xl border-dashed" onClick={addSection} data-testid="add-section-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Section
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <TemplatePreview template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
    </div>
  );
};

export default DocumentTemplates;
